import * as THREE from 'three';
import { createBoat, type BoatModel } from '../assets/modelFactories/BoatFactory';

export type BoatTuning = {
  maxSpeed: number;
  acceleration: number;
  turnRate: number;
  drag: number;
  lateralGrip: number;
  boostSpeed: number;
  boostDrain: number;
  boostRegen: number;
};

export const DEFAULT_BOAT_TUNING: BoatTuning = {
  maxSpeed: 26,
  acceleration: 14,
  turnRate: 1.9,
  drag: 0.35,
  lateralGrip: 4.2,
  boostSpeed: 44,
  boostDrain: 34,
  boostRegen: 9,
};

// Ocean wave sampler shared with rendering (same wave set as the shader).
export function sampleWave(x: number, z: number, t: number): { y: number; slopeX: number; slopeZ: number } {
  const waves: Array<[number, number, number, number]> = [
    // wavelength, steepness, dirX, dirZ — must mirror the shader set
    [34, 0.3, 1, 0.3],
    [17, 0.22, -0.7, 0.6],
    [9, 0.12, 0.4, -0.9],
    [5, 0.08, 0.9, 0.8],
    [3, 0.06, -0.8, -0.5],
    [60, 0.42, 0.15, 0.99],
  ];
  let y = 0;
  let sx = 0;
  let sz = 0;
  for (const [wl, steep, dx, dz] of waves) {
    const k = (2 * Math.PI) / wl;
    const c = Math.sqrt(9.8 / k);
    const d = Math.hypot(dx, dz);
    const ux = dx / d;
    const uz = dz / d;
    const f = k * (ux * x + uz * z - c * t);
    const a = steep / k;
    y += a * Math.sin(f);
    sx += ux * steep * Math.cos(f);
    sz += uz * steep * Math.cos(f);
  }
  return { y, slopeX: sx, slopeZ: sz };
}

export class Boat {
  readonly group = new THREE.Group();
  readonly velocity = new THREE.Vector2();
  readonly model: BoatModel;
  boost = 1;
  boostActive = false;

  private heading = 0;
  private readonly smoothMove = new THREE.Vector2();

  constructor(tuning: BoatTuning) {
    this.model = createBoat();
    this.group.add(this.model.root);
    this.tuning = tuning;
  }
  private tuning: BoatTuning;

  get speed(): number {
    return this.velocity.length();
  }

  get headingAngle(): number {
    return this.heading;
  }

  update(
    delta: number,
    elapsed: number,
    move: THREE.Vector2,
    boostHeld: boolean,
  ): void {
    const t = this.tuning;

    // Heading: steer by stick x; more authority at speed.
    this.smoothMove.lerp(move, 1 - Math.exp(-8 * delta));
    const speedFactor = THREE.MathUtils.clamp(this.speed / (t.maxSpeed * 0.42), 0, 1);
    const steer = -this.smoothMove.x * t.turnRate * (0.35 + 0.65 * speedFactor);
    this.heading += steer * delta;

    // Boost
    if (boostHeld && this.boost > 0.05 && this.speed > 1) {
      this.boostActive = true;
      this.boost = Math.max(0, this.boost - t.boostDrain * delta);
    } else {
      this.boostActive = false;
      this.boost = Math.min(1, this.boost + t.boostRegen * delta);
    }

    // Forward thrust along heading; reverse is weak.
    const targetSpeed = this.boostActive ? t.boostSpeed : t.maxSpeed * Math.max(0, -this.smoothMove.y);
    const fwd = new THREE.Vector2(Math.sin(this.heading), Math.cos(this.heading));
    const forwardSpeed = this.velocity.dot(fwd);
    const newForward = THREE.MathUtils.lerp(forwardSpeed, targetSpeed, 1 - Math.exp(-t.acceleration * delta));

    // Lateral velocity decays (grip); some retained for drift feel.
    const side = new THREE.Vector2(fwd.y, -fwd.x);
    const lateralSpeed = this.velocity.dot(side);
    const newLateral = lateralSpeed * Math.exp(-t.lateralGrip * delta);

    const forwardSpeedNorm = Math.min(1, Math.max(0, newForward) / t.maxSpeed);
    this.velocity.copy(fwd).multiplyScalar(newForward).addScaledVector(side, newLateral);
    if (this.speed > t.boostSpeed * 1.02) {
      this.velocity.multiplyScalar((t.boostSpeed * 1.02) / this.speed);
    }

    this.group.position.x += this.velocity.x * delta;
    this.group.position.z += this.velocity.y * delta;
    this.group.rotation.y = this.heading;

    // Buoyancy: sample the same wave field under the hull.
    const wave = sampleWave(this.group.position.x, this.group.position.z, elapsed);
    const bobY = wave.y * 0.55;
    this.group.position.y += (bobY - this.group.position.y) * Math.min(1, delta * 6);

    // Attitude: bow rises with speed (planing), carves INTO turns at low speed,
    // leans OUT (powerslide chicane) at high speed. Turn input flips lean sign past 60% speed.
    const planing = Math.min(1, forwardSpeedNorm * 1.35) * 0.14;
    const slideSign = forwardSpeedNorm > 0.6 ? -0.55 : 1;
    const pitch = -wave.slopeZ * Math.cos(this.heading) * 0.6 + (this.boostActive ? 0.07 : 0) + planing;
    const roll = this.smoothMove.x * speedFactor * 0.32 * slideSign + wave.slopeX * 0.4;
    this.group.rotation.x += (pitch - this.group.rotation.x) * Math.min(1, delta * 4);
    this.group.rotation.z += (roll * 0.55 - this.group.rotation.z) * Math.min(1, delta * 5);

    // Engine visuals: prop spin + boost flames.
    const spin = delta * (14 + this.speed * 1.6);
    for (const eng of this.model.engines) {
      eng.children[3].rotation.y += spin * 8;
    }
    for (const flame of this.model.throttleFlames) {
      flame.visible = this.boostActive;
      if (this.boostActive) {
        const s = 0.8 + Math.sin(elapsed * 40) * 0.2;
        flame.scale.set(s, 1 + s * 0.4, s);
      }
    }
  }

  snapTo(x: number, z: number, heading: number): void {
    this.group.position.set(x, 0, z);
    this.group.rotation.set(0, heading, 0);
    this.heading = heading;
    this.velocity.set(0, 0);
    this.boost = 1;
    this.boostActive = false;
  }

  dispose(): void {
    this.group.clear();
  }
}
